import { useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { fetchAuthOpts, formatApiError, STATUS_LABELS, STATUS_ORDER } from "@/lib/api";
import { StatusPill } from "@/components/StatusPill";
import { RepairSearchAutocomplete } from "@/components/RepairSearchAutocomplete";
import { formatMadridDateTime } from "@/lib/datetime";
import {
  ArrowLeft, Save, Trash2, Eye, EyeOff,
  FileDown, Mail, Receipt, Truck,
  History, ChevronLeft, ChevronRight, ChevronsRight,
  RotateCcw, Tag, ClipboardCheck, Check, Send, MessageCircle, CreditCard,
} from "lucide-react";
import { toast } from "sonner";
import ChecklistModal from "@/components/ChecklistModal";
import ChecklistIncidentsModal from "@/components/repairs/ChecklistIncidentsModal";
import ChecklistOfferModal from "@/components/repairs/ChecklistOfferModal";
import CrossSellModal from "@/components/CrossSellModal";
import ReservationModal from "@/components/repairs/ReservationModal";
import SignatureModal from "@/components/SignatureModal";
import Box from "@/components/repairs/Box";
import BoxItems from "@/components/repairs/BoxItems";
import BoxDocuments from "@/components/repairs/BoxDocuments";
import BoxSerialHistory from "@/components/repairs/BoxSerialHistory";
import TimelineModal from "@/components/repairs/TimelineModal";
import RepairStatusTimeline from "@/components/repairs/RepairStatusTimeline";
import SituationDialog, { SituationBadge } from "@/components/repairs/SituationDialog";
import { useAuth } from "@/context/AuthContext";
import CustomerMessageBox from "@/components/repairs/CustomerMessageBox";
import NotifyCustomerDialog from "@/components/repairs/NotifyCustomerDialog";
import EditableCatalogSelect from "@/components/repairs/EditableCatalogSelect";
import CustomerPicker from "@/components/repairs/CustomerPicker";
import DeviceModelSelect from "@/components/repairs/DeviceModelSelect";
import ContactChannelSelect from "@/components/repairs/ContactChannelSelect";
import { Field, RO, Dialog } from "@/components/repairs/RepairFormAtoms";
import useUnsavedChanges from "@/hooks/useUnsavedChanges";
import UnsavedDot from "@/components/UnsavedDot";

const emptyForm = {
  customer_id: "",
  device_type: "",
  model: "",
  serial: "",
  issue: "",
  diagnosis: "",  // Iter 161 — Diagnóstico técnico
  accessories: "",
  customer_reference: "",
  section: "",
  customer_contact_channel: "",  // iter 85
  device_password: "",
  password_status: "",
  backup_status: "",
  assigned_to: "",
  technical_comment: "",
  estimated_cost: 0,
  scheduled_at: "",
  scheduled_duration_min: 60,
};

const emptyCustomer = { name: "", phone: "", email: "", tax_id: "" };

// Iter 161 — Fecha estimada: "YYYY-MM-DD" (día completo) o "YYYY-MM-DDTHH:MM" (hora local).
// Valores legados en UTC ("...Z") se convierten a hora local.
function normalizeScheduled(v) {
  if (!v) return "";
  if (/Z$|[+-]\d\d:\d\d$/.test(v)) {
    const d = new Date(v);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
  return v.length > 16 ? v.substring(0, 16) : v;
}

export default function RepairForm({ mode }) {
  const isNew = mode === "new";
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [order, setOrder] = useState(null);
  const [form, setForm] = useState({
    ...emptyForm,
  });
  const [customer, setCustomer] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [deviceCatalog, setDeviceCatalog] = useState([]);
  const [contactChannelCatalog, setContactChannelCatalog] = useState([]);
  const [sectionCatalog, setSectionCatalog] = useState([]);
  const [pwdCatalog, setPwdCatalog] = useState([]);
  const [backupCatalog, setBackupCatalog] = useState([]);
  const [showPwd, setShowPwd] = useState(false);
  const [saving, setSaving] = useState(false);
  // Iter 92 — Guardia sincrónica contra doble-click: `setSaving` es asíncrono,
  // por lo que un segundo click puede ejecutar save() de nuevo antes del re-render.
  const savingRef = useRef(false);
  // Iter 92 — Blindaje adicional: id de la reparación recién creada en el
  // flujo isNew. Si save() se vuelve a invocar (por ejemplo desde
  // onSaveAndExit del useUnsavedChanges antes de que React desmonte este
  // componente y navegue al nuevo id), este ref impide un segundo POST.
  const createdIdRef = useRef(null);
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [newCustomer, setNewCustomer] = useState(emptyCustomer);
  const [dirty, setDirty] = useState(false);
  const [neighbors, setNeighbors] = useState({ prev: null, next: null });
  const [showReservation, setShowReservation] = useState(false);
  // Iter 139 — Última reserva pagada (badge "✓ RESERVA PAGADA" en la toolbar).
  const [paidReservation, setPaidReservation] = useState(null);
  const [rmaConfirm, setRmaConfirm] = useState(false);
  const [creatingRma, setCreatingRma] = useState(false);
  const [showChecklist, setShowChecklist] = useState(false);
  // Iter 169 — Checklist negativo → incidencias pendientes → oferta con 20 % dto
  const [incidents, setIncidents] = useState(null);
  const [offerIncidents, setOfferIncidents] = useState(null);
  const checkIncidents = async () => {
    if (!id) return;
    try {
      const r = await api.get(`/repairs/${id}/checklist/incidents`);
      if ((r.data.pending || []).length) setIncidents(r.data.pending);
    } catch { /* silencioso */ }
  };
  // Iter 126 — Color del botón Checklist: 'ok'|'warning'|'critical'.
  // Se lee del `order.checklist_alert_level` y se refresca tras guardar el
  // checklist. Nunca depende de otra reparación.
  const [checklistAlert, setChecklistAlert] = useState("");
  // Iter 129 — Modal de venta cruzada. Se abre SOLO después de crear la
  // reparación con éxito y sólo si hay una regla activa para Equipo+Modelo.
  // Nunca se abre al editar (edición no dispara flujo de venta cruzada).
  const [crossSell, setCrossSell] = useState(null);
  // { repairId, rule, products, modelLabel, pendingNav }
  const [showSignature, setShowSignature] = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);
  const [showNotify, setShowNotify] = useState(false);
  const [showSituation, setShowSituation] = useState(false);
  const { user: me } = useAuth();
  const skipDirtyRef = useRef(true);

  // Iter 126 — Sincronizar el color del botón Checklist con la reparación
  // cargada. Se re-ejecuta al cambiar `order` (edición manual, refresh...).
  useEffect(() => {
    setChecklistAlert(order?.checklist_alert_level || "");
  }, [order?.id, order?.checklist_alert_level]);

  const loadCatalogs = async () => {
    const [t, s] = await Promise.all([
      api.get("/technicians"),
      api.get("/settings"),
    ]);
    setTechnicians(t.data);
    setDeviceCatalog(s.data.device_catalog || []);
    setContactChannelCatalog(s.data.contact_channel_catalog || []);
    // Iter 98 — sectionCatalog es ahora `[{name, status, active}]`. El
    // filtrado por estado se hace en `visibleSectionNames` (useMemo abajo).
    setSectionCatalog(s.data.section_catalog || []);
    setPwdCatalog(s.data.password_status_catalog || []);
    setBackupCatalog(s.data.backup_status_catalog || []);
  };

  // Iter 98 — Estado actual de la reparación (para filtrar secciones).
  // En creación no hay `order` todavía → usamos "recibido" como default.
  const currentStatus = order?.status || "recibido";

  // Iter 98 — Nombres de sección disponibles para el estado actual.
  // Reglas:
  //   • Se muestran las secciones cuyo `status` coincide con el estado actual.
  //   • Se muestran también las secciones GLOBALES (status="") — legacy /
  //     sin asignar, siempre elegibles.
  //   • Se filtran las secciones inactivas.
  //   • Si `form.section` no está en la lista, se añade igualmente para NO
  //     perder la sección heredada de reparaciones antiguas (requisito
  //     explícito: no modificar datos existentes).
  const visibleSectionNames = useMemo(() => {
    const list = (sectionCatalog || []).filter(
      (s) => s && s.active !== false && (s.status === currentStatus || !s.status),
    );
    const names = list.map((s) => s.name);
    if (form.section && !names.includes(form.section)) {
      names.push(form.section); // preserva sección legacy que no coincide
    }
    return names;
  }, [sectionCatalog, currentStatus, form.section]);

  // Iter 98 — Callback para "Gestionar secciones" desde el select inline.
  // Recibe la lista modificada de NOMBRES (solo del estado actual). Debemos:
  //   • Preservar secciones de otros estados y las globales.
  //   • Añadir las nuevas asignándoles el `currentStatus`.
  //   • Quitar las que fueron eliminadas de la lista visible pero solo
  //     dentro del scope `currentStatus` (no borran las de otros estados).
  const handleSectionItemsChange = (newNames) => {
    const currentSet = new Set(newNames);
    const preserved = (sectionCatalog || []).filter(
      (s) => s && (s.status !== currentStatus || !s.status) && s.active !== false,
    );
    // Reconstruye la parte del currentStatus con las secciones ya presentes
    // (para mantener `active`/otros meta) o crea entrada nueva si no existía.
    const rebuiltThisStatus = newNames.map((name) => {
      const existing = (sectionCatalog || []).find(
        (s) => s && s.name === name && s.status === currentStatus,
      );
      return existing || { name, status: currentStatus, active: true };
    });
    const finalList = [...preserved, ...rebuiltThisStatus];
    void updateCatalog("section_catalog", finalList);
    // Optimistic local update
    setSectionCatalog(finalList);
    // Si la sección actual del formulario fue borrada, limpia el valor.
    if (form.section && !currentSet.has(form.section)) {
      const stillGlobal = preserved.some(
        (s) => s.name === form.section && !s.status,
      );
      if (!stillGlobal) set("section", "");
    }
  };

  const loadCustomer = async (cid) => {
    if (!cid) {
      setCustomer(null);
      return;
    }
    try {
      const r = await api.get(`/customers/${cid}`);
      setCustomer(r.data);
    } catch {
      setCustomer(null);
    }
  };

  const loadCustomers = async () => {
    const r = await api.get("/customers");
    setCustomers(r.data);
  };

  useEffect(() => {
    loadCatalogs();
    if (isNew) {
      loadCustomers();
      const preCustomer = searchParams.get("customer");
      if (preCustomer) {
        setForm((f) => ({ ...f, customer_id: preCustomer }));
        loadCustomer(preCustomer);
      }
      setNeighbors({ prev: null, next: null });
    } else {
      skipDirtyRef.current = true;
      api.get(`/repairs/${id}`).then((r) => {
        const o = r.data;
        setOrder(o);
        skipDirtyRef.current = true;
        setForm({
          customer_id: o.customer_id,
          device_type: o.device_type || "",
          model: o.model || "",
          serial: o.serial || "",
          issue: o.issue || "",
          diagnosis: o.diagnosis || "",
          accessories: o.accessories || "",
          customer_reference: o.customer_reference || "",
          section: o.section || "",
          customer_contact_channel: o.customer_contact_channel || "",
          device_password: o.device_password || "",
          password_status: o.password_status || "",
          backup_status: o.backup_status || "",
          assigned_to: o.assigned_to || "",
          technical_comment: "",
          estimated_cost: o.estimated_cost || 0,
          scheduled_at: normalizeScheduled(o.scheduled_at),
          scheduled_duration_min: o.scheduled_duration_min || 60,
        });
        loadCustomer(o.customer_id);
        setDirty(false);
        // Sólo abrir el modal de firma si la reparación NO tiene firma todavía.
        // "pending" o vacío → pedir firma. Cualquier otro estado → respetar
        // (firmada presencial/remota, awaiting_remote, discarded_company).
        const ss = o.signature_status || "pending";
        const needsSignature = ss === "pending";
        if (searchParams.get("openSignature") === "1" && needsSignature) {
          setShowSignature(true);
        }
      });
      api.get(`/repairs/${id}/neighbors`).then((r) => setNeighbors(r.data)).catch(() => setNeighbors({ prev: null, next: null }));
      // Iter 139 — Cargar reservas para detectar si ya se pagó una.
      api.get(`/repairs/${id}/reservations`)
        .then((r) => {
          const paid = (r.data || []).find((x) => x.status === "completed");
          setPaidReservation(paid || null);
        })
        .catch(() => setPaidReservation(null));
    }
    // El effect sólo debe re-disparar si cambia la reparación (id/isNew). El
    // resto de deps son setters estables y searchParams — añadirlas dispararía
    // recargas indeseadas al cambiar la query string.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  // Track dirty state when form fields change
  useEffect(() => {
    if (skipDirtyRef.current) {
      skipDirtyRef.current = false;
      return;
    }
    setDirty(true);
  // Dependencias enumeradas explícitamente; `skipDirtyRef` es ref (estable).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.device_type, form.model, form.serial, form.issue, form.accessories,
      form.customer_reference, form.section, form.customer_contact_channel,
      form.device_password, form.password_status,
      form.backup_status,
      form.assigned_to, form.estimated_cost, form.customer_id]);

  const set = (k, v) => setForm({ ...form, [k]: v });

  // Lista de técnicos activos — memoizada (re-render del form no debe recalcularla).
  const activeTechnicians = useMemo(
    () => technicians.filter((t) => t.active),
    [technicians],
  );

  const updateCatalog = async (key, newList) => {
    try {
      await api.put("/settings", { [key]: newList });
      if (key === "device_catalog") setDeviceCatalog(newList);
      if (key === "section_catalog") setSectionCatalog(newList);
      if (key === "backup_status_catalog") setBackupCatalog(newList);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const save = async () => {
    if (!form.serial || !form.serial.trim()) {
      toast.error("Debes introducir el número de serie o IMEI del dispositivo.");
      return false;
    }
    if (isNew && !form.customer_id) {
      toast.error("Selecciona un cliente.");
      return false;
    }
    if (isNew && !form.device_type) {
      toast.error("Indica el tipo de equipo.");
      return false;
    }
    if (
      (form.password_status || "").trim().toLowerCase() === "cliente deja contraseña" &&
      !(form.device_password || "").trim()
    ) {
      toast.error("Debes introducir la contraseña del cliente cuando el estado es 'Cliente deja contraseña'.");
      return false;
    }
    if (!(form.backup_status || "").trim()) {
      toast.error("Debes seleccionar una opción de copia de seguridad.");
      return false;
    }
    if (savingRef.current) return false; // Iter 92 — bloquea reentradas del onClick
    // Iter 92 — si ya se creó una reparación en esta sesión del formulario,
    // NO permitimos otro POST (evita duplicado si onSaveAndExit del blocker
    // llama a save() otra vez tras el primer POST exitoso).
    if (isNew && createdIdRef.current) return true;
    savingRef.current = true;
    setSaving(true);
    try {
      if (isNew) {
        const payload = {
          customer_id: form.customer_id,
          device_type: form.device_type,
          model: form.model,
          serial: form.serial.trim(),
          issue: form.issue,
          diagnosis: form.diagnosis,
          accessories: form.accessories,
          customer_reference: form.customer_reference,
          section: form.section,
          customer_contact_channel: form.customer_contact_channel,
          device_password: form.device_password,
          password_status: form.password_status,
          backup_status: form.backup_status,
          assigned_to: form.assigned_to || null,
          technical_comment: form.technical_comment,
          estimated_cost: Number(form.estimated_cost) || 0,
          scheduled_at: form.scheduled_at || null,
          scheduled_duration_min: Number(form.scheduled_duration_min) || 60,
        };
        const r = await api.post("/repairs", payload);
        toast.success(`Orden ${r.data.code} creada`);
        // Iter 111 — Feedback del email automático "Recepción sin presupuesto".
        const rxStatus = r.data?.reception_email_status || "";
        if (rxStatus === "sent") {
          toast.success(`Email de recepción enviado a ${r.data.reception_email_sent_to}`);
        } else if (rxStatus === "skipped_missing_data") {
          toast.warning("La reparación se ha guardado, pero no se envió el email porque faltan datos obligatorios.");
        } else if (rxStatus === "error") {
          toast.error("La reparación se guardó, pero el email automático no se pudo enviar.");
        }
        createdIdRef.current = r.data.id; // Iter 92 — bloquea segundas creaciones
        // Iter 92 — flushSync fuerza el rerender ANTES de navigate() para que
        // dirty=false en useBlocker: evita que el modal "cambios sin guardar"
        // se dispare tras el primer POST exitoso (que si el usuario confirmaba
        // Guardar-y-salir volvía a llamar a save() creando un DUPLICADO).
        flushSync(() => setDirty(false));
        // Iter 129 — Buscar regla activa de venta cruzada para Equipo+Modelo.
        // Si existe: mostrar el modal BLOQUEANTE antes de navegar. La
        // reparación YA está creada — el modal solo añade productos al
        // repair_id existente, nunca vuelve a crear.
        const pendingNav = `/repairs/${r.data.id}?openSignature=1`;
        try {
          const mr = await api.get(
            `/cross-sell/match?device_type=${encodeURIComponent(form.device_type)}` +
            `&model=${encodeURIComponent(form.model)}`,
          );
          if (mr.data?.rule && (mr.data?.products || []).length > 0) {
            setCrossSell({
              repairId: r.data.id,
              rule: mr.data.rule,
              products: mr.data.products,
              modelLabel: `${form.device_type} ${form.model}`.trim(),
              pendingNav,
            });
            return true; // El modal decidirá cuándo navegar (onFinished).
          }
        } catch (_e) {
          // Nunca bloqueamos la creación por un error en la búsqueda de reglas.
        }
        navigate(pendingNav);
        return true;
      } else {
        const payload = {
          device_type: form.device_type,
          model: form.model,
          serial: form.serial.trim(),
          issue: form.issue,
          diagnosis: form.diagnosis,
          accessories: form.accessories,
          customer_reference: form.customer_reference,
          section: form.section,
          customer_contact_channel: form.customer_contact_channel,
          device_password: form.device_password,
          password_status: form.password_status,
          backup_status: form.backup_status,
          assigned_to: form.assigned_to || null,
          estimated_cost: Number(form.estimated_cost) || 0,
          scheduled_at: form.scheduled_at || null,
          scheduled_duration_min: Number(form.scheduled_duration_min) || 60,
        };
        const r = await api.put(`/repairs/${id}`, payload);
        setOrder(r.data);
        setDirty(false);
        toast.success("Reparación guardada");
        // Abrir el modal de firma SOLO si la reparación está aún "pending".
        // Cualquier otro estado (signed_presential, signed_remote, awaiting_remote,
        // discarded_company) → respetar la decisión previa y no volver a pedir firma.
        const ss = r.data?.signature_status || "pending";
        if (ss === "pending") {
          setShowSignature(true);
        }
        return true;
      }
    } catch (err) {
      toast.error(formatApiError(err));
      return false;
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  // Iter 82 — Guardia de cambios sin guardar (navegación interna + cierre de pestaña).
  const unsaved = useUnsavedChanges({ dirty, onSaveAndExit: save });

  const del = async () => {
    if (!window.confirm("¿Eliminar esta orden de reparación?")) return;
    // Detectar si tiene líneas con stock ya descontado
    const hasConsumed = (order?.items || []).some((it) => it.stock_consumed && it.product_id);
    let returnStock = false;
    if (hasConsumed) {
      const r = window.confirm("Esta reparación tiene repuestos con stock ya descontado. ¿Devolverlos al inventario?\n\n• Aceptar = SÍ, repongo el stock\n• Cancelar = NO, dejo el stock como está");
      returnStock = r;
    }
    await api.delete(`/repairs/${id}`, { params: { return_stock: returnStock } });
    toast.success(returnStock ? "Orden eliminada y stock devuelto" : "Orden eliminada");
    navigate("/repairs");
  };

  const changeStatus = async (newStatus) => {
    try {
      // Iter 98 — al cambiar estado, si la sección actual no pertenece al
      // nuevo estado (ni es global), la limpiamos para forzar al usuario a
      // reasignarla desde el desplegable ya filtrado.
      const belongsToNew = (sectionCatalog || []).some(
        (s) => s && s.name === form.section &&
          (s.status === newStatus || !s.status) && s.active !== false,
      );
      const patch = { status: newStatus };
      if (form.section && !belongsToNew) {
        patch.section = "";
      }
      const r = await api.put(`/repairs/${id}`, patch);
      setOrder(r.data);
      if (patch.section === "") {
        set("section", "");
        toast.info("La sección se ha limpiado porque no pertenece al nuevo estado.");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const createInlineCustomer = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const r = await api.post("/customers", newCustomer);
      toast.success(`Cliente ${r.data.code} creado`);
      await loadCustomers();
      set("customer_id", r.data.id);
      setCustomer(r.data);
      setShowNewCustomer(false);
      setNewCustomer(emptyCustomer);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // ===== Navegación: Anterior / Siguiente =====
  const goNeighbor = (target) => {
    if (!target) return;
    navigate(`/repairs/${target.id}`);
  };

  // ===== Iter 84 — Abrir chat de WhatsApp con el cliente =====
  // Normaliza el teléfono guardado (quita espacios, guiones, paréntesis, puntos).
  // Si no lleva prefijo internacional (+ o 00), asume España (+34).
  const openWhatsApp = () => {
    const raw = (customer?.phone || "").trim();
    if (!raw) {
      toast.error("Este cliente no tiene teléfono guardado.");
      return;
    }
    // Detectar prefijo internacional antes de limpiar.
    let hasPlus = raw.startsWith("+");
    let cleaned = raw.replace(/[\s\-()./]/g, "");
    if (cleaned.startsWith("00")) {
      hasPlus = true;
      cleaned = cleaned.slice(2);
    } else if (cleaned.startsWith("+")) {
      cleaned = cleaned.slice(1);
    }
    // Solo dígitos: si no hay prefijo y son 9 dígitos, asumimos España.
    const digits = cleaned.replace(/\D/g, "");
    if (!digits || digits.length < 6 || digits.length > 15) {
      toast.error("El número de teléfono del cliente no parece válido.");
      return;
    }
    const finalNumber = hasPlus ? digits : (digits.length === 9 ? `34${digits}` : digits);
    window.open(`https://wa.me/${finalNumber}`, "_blank", "noopener,noreferrer");
  };

  // ===== RMA =====
  const doRma = async () => {
    if (isNew || !id) return;
    if (dirty) {
      if (!window.confirm("Hay cambios sin guardar. ¿Crear la RMA igualmente (los cambios se perderán)?")) {
        return;
      }
    }
    setCreatingRma(true);
    try {
      const r = await api.post(`/repairs/${id}/rma`);
      toast.success(`RMA ${r.data.code} creada`);
      setRmaConfirm(false);
      setDirty(false);
      navigate(`/repairs/${r.data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCreatingRma(false);
    }
  };

  // ===== Etiqueta 50×30 mm =====
  const downloadLabel = async () => {
    if (isNew || !id) {
      toast.info("Guarda primero la reparación.");
      return;
    }
    try {
      const url = `${api.defaults.baseURL}/repairs/${id}/label-pdf`;
      const r = await fetch(url, fetchAuthOpts());
      if (!r.ok) {
        toast.error("Error al generar la etiqueta");
        return;
      }
      const blob = await r.blob();
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, "_blank");
    } catch {
      toast.error("Error al descargar la etiqueta");
    }
  };

  return (
    <div>
      {/* Top action bar (sticky, glassmorphism) */}
      <div className="ds-actionbar px-6 lg:px-8 py-3 flex items-center gap-2 flex-wrap" style={{ top: 0 }}>
        <Link
          to="/repairs"
          data-testid="repair-back"
          className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[.14em] text-[#4B5158] hover:text-[#3F5222] font-semibold transition-colors"
        >
          <ArrowLeft size={12} /> Volver al listado
        </Link>
        {!isNew && (
          <>
            <div className="w-px h-[22px] bg-[#E6E7E1] mx-1"/>
            <div className="ds-pager">
              <button
                type="button"
                onClick={() => goNeighbor(neighbors.prev)}
                disabled={!neighbors.prev}
                data-testid="repair-nav-prev"
                title={neighbors.prev ? `Anterior · ${neighbors.prev.code}` : "Sin reparación anterior"}
              >
                <ChevronLeft size={13} /> Anterior
              </button>
              <button
                type="button"
                onClick={() => goNeighbor(neighbors.next)}
                disabled={!neighbors.next}
                data-testid="repair-nav-next"
                title={neighbors.next ? `Siguiente · ${neighbors.next.code}` : "Sin reparación siguiente"}
              >
                Siguiente <ChevronRight size={13} />
              </button>
              <button
                type="button"
                onClick={() => goNeighbor(neighbors.last)}
                disabled={!neighbors.last}
                data-testid="repair-nav-last"
                title={neighbors.last ? `Última creada · ${neighbors.last.code}` : "Ya estás en la última reparación creada"}
              >
                Última <ChevronsRight size={13} />
              </button>
            </div>
          </>
        )}
        {dirty && !isNew && (
          <>
            <div className="w-px h-[22px] bg-[#E6E7E1] mx-1"/>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#FFF7E6] text-[#8C5A00] text-[11px] font-semibold">
              <span className="ds-dot-pulse"/>Cambios sin guardar
            </span>
          </>
        )}

        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <RepairSearchAutocomplete className="w-full sm:w-72" testid="repair-ficha-search" />
          {!isNew && order && (
            <>
              <button
                type="button"
                onClick={() => setShowChecklist(true)}
                data-testid="repair-checklist-btn"
                data-checklist-alert={checklistAlert || "ok"}
                title={
                  checklistAlert === "critical"
                    ? "Checklist con fallos críticos"
                    : checklistAlert === "warning"
                    ? "Checklist con advertencias"
                    : "Checklist 'Montaje del Equipo y Pruebas'"
                }
                className={
                  checklistAlert === "critical"
                    ? "ds-btn"
                    : checklistAlert === "warning"
                    ? "ds-btn"
                    : "ds-btn"
                }
                style={
                  checklistAlert === "critical"
                    ? { background: "linear-gradient(180deg,#e26b4d,#b4381f)", color: "#fff", borderColor: "transparent", boxShadow: "0 4px 14px rgba(180,56,31,.3)" }
                    : checklistAlert === "warning"
                    ? { background: "linear-gradient(180deg,#f2c169,#d68a00)", color: "#0F1210", borderColor: "transparent", boxShadow: "0 4px 14px rgba(214,138,0,.25)" }
                    : {}
                }
              >
                <ClipboardCheck size={13} /> Check
              </button>
              <button
                type="button"
                onClick={downloadLabel}
                data-testid="repair-label-btn"
                title="Generar etiqueta 50×30 mm para impresora"
                className="ds-btn"
              >
                <Tag size={13} /> Etiqueta
              </button>
              {paidReservation ? (
                <div
                  data-testid="repair-reservation-paid-badge"
                  title={`Reserva pagada · ${paidReservation.paypal_capture_id || paidReservation.paypal_order_id || ""}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11.5px] font-semibold"
                  style={{ background: "#E6F6EE", color: "#136A34", border: "1px solid rgba(34,160,91,.22)" }}
                >
                  <CreditCard size={13} />
                  ✓ Reserva pagada · {Number(paidReservation.amount_paid || paidReservation.amount || 0).toFixed(2).replace(".", ",")} €
                  {paidReservation.paid_at && (
                    <span className="opacity-75 font-medium ml-1">
                      · {new Date(paidReservation.paid_at).toLocaleString("es-ES", { timeZone: "Europe/Madrid", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </span>
                  )}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowReservation(true)}
                  data-testid="repair-reservation-btn"
                  title="Generar reserva con enlace PayPal"
                  className="ds-btn warn"
                >
                  <CreditCard size={13} /> Reserva
                </button>
              )}
              <button
                type="button"
                onClick={() => setRmaConfirm(true)}
                data-testid="repair-rma-btn"
                title="Crear RMA / devolución a partir de esta reparación"
                className="ds-btn danger"
              >
                <RotateCcw size={13} /> RMA
              </button>
              <button
                onClick={del}
                data-testid="repair-delete"
                className="ds-btn danger"
              >
                <Trash2 size={14} /> Eliminar
              </button>
            </>
          )}
          <button
            onClick={save}
            disabled={saving}
            data-testid="repair-save"
            className="ds-btn primary"
          >
            <UnsavedDot show={dirty} />
            <Save size={14} /> {saving ? "Guardando..." : isNew ? "Crear reparación" : "Guardar"}
          </button>
        </div>
      </div>

      <div className="px-6 lg:px-8 py-5 max-w-[1500px] mx-auto">

      {/* Hero card */}
      <div className="ds-card ds-hero-glow px-6 py-5 mt-1" data-testid="repair-hero">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="font-mono text-[11px] uppercase tracking-[.14em] text-[#3F5222] font-semibold">
              <span className="text-[#8A9098]">//</span>&nbsp;&nbsp;
              {isNew ? "Nueva reparación" : (
                <>
                  Ficha de reparación &nbsp;<span className="text-[#8A9098]">·</span>&nbsp;
                  <span className="text-[#3F5222]">{order?.code || ""}</span>
                </>
              )}
            </div>
            <h1 className="font-display font-black text-4xl md:text-[42px] mt-2 tracking-tight leading-none">
              {isNew
                ? "Crear orden de reparación"
                : (order?.model || "").trim() || (order?.device_type || "").trim() || (order?.code || "")}
            </h1>
            {!isNew && order && (
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <StatusPill status={order.status} />
                {customer?.name && (
                  <span className="text-[13px] text-[#4B5158]">
                    <span className="text-[#8A9098] mx-1">·</span>{customer.name}
                  </span>
                )}
                {order.rma_source_id && (
                  <Link
                    to={`/repairs/${order.rma_source_id}`}
                    data-testid="repair-rma-source-link"
                    className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider border border-amber-700 text-amber-800 bg-amber-50 hover:bg-amber-100 rounded-full transition-colors"
                    title="Ver reparación origen de esta RMA"
                  >
                    <RotateCcw size={11} /> RMA de origen
                  </Link>
                )}
                {order.rma_target_id && (
                  <Link
                    to={`/repairs/${order.rma_target_id}`}
                    data-testid="repair-rma-target-link"
                    className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider border border-amber-700 text-amber-800 bg-amber-50 hover:bg-amber-100 rounded-full transition-colors"
                    title="Ver RMA creada desde esta reparación"
                  >
                    <RotateCcw size={11} /> Ver RMA →
                  </Link>
                )}
                {order.signature_status && order.signature_status !== "pending" && order.signature?.signed_at && (
                  <button
                    type="button"
                    onClick={() => setShowSignature(true)}
                    data-testid="repair-signature-badge"
                    title={`Firmado ${order.signature_status === "signed_remote" ? "remotamente" : "presencialmente"} el ${new Date(order.signature.signed_at).toLocaleString("es-ES")}. Pulsa para ver detalles.`}
                    className="inline-flex items-center gap-1.5 px-2.5 py-0.5 text-[10.5px] font-semibold border border-[#c8d6a5] text-[#3F5222] bg-[#EFF3E6] hover:bg-[#E4EDD3] rounded-full transition-colors"
                  >
                    <Check size={11} strokeWidth={3} />
                    Firmado · {new Date(order.signature.signed_at).toLocaleDateString("es-ES")}
                  </button>
                )}
                {order.signature_status === "discarded_company" && (
                  <button
                    type="button"
                    onClick={() => setShowSignature(true)}
                    data-testid="repair-signature-discarded-badge"
                    title="Firma descartada por cliente empresa. Pulsa para revisar o reactivar."
                    className="inline-flex items-center gap-1.5 px-2.5 py-0.5 text-[10.5px] font-semibold border border-[#D5D7CE] text-[#4B5158] bg-[#F5F6F2] hover:bg-[#EEF0EA] rounded-full transition-colors"
                  >
                    <Check size={11} strokeWidth={3} />
                    Cliente empresa · firma descartada
                  </button>
                )}
                {order.signature_status === "awaiting_remote" && (
                  <button
                    type="button"
                    onClick={() => setShowSignature(true)}
                    data-testid="repair-signature-awaiting-badge"
                    title="Pendiente de firma remota del cliente. Pulsa para ver el enlace."
                    className="inline-flex items-center gap-1.5 px-2.5 py-0.5 text-[10.5px] font-semibold border border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100 rounded-full transition-colors"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                    Pendiente firma remota
                  </button>
                )}
                {(!order.signature_status || order.signature_status === "pending") && (
                  <button
                    type="button"
                    onClick={() => setShowSignature(true)}
                    data-testid="repair-signature-pending-badge"
                    title="Pulsa para solicitar la firma del cliente"
                    className="inline-flex items-center gap-1.5 px-2.5 py-0.5 text-[10.5px] font-semibold border border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100 rounded-full transition-colors"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                    Sin firmar
                  </button>
                )}
                {order.situation_main && (
                  <button
                    type="button"
                    onClick={() => setShowSituation(true)}
                    title="Situación de la reparación. Pulsa para editar."
                    className="inline-flex"
                  >
                    <SituationBadge situation={{
                      main: order.situation_main,
                      detail_label: order.situation_detail_label,
                    }} />
                  </button>
                )}
              </div>
            )}
          </div>

          {!isNew && order && (
            <div
              className="grid grid-cols-1 sm:grid-cols-3 gap-3 w-full md:w-auto md:min-w-[440px] lg:min-w-[490px]"
              data-testid="repair-meta-row"
            >
              <div>
                <EditableCatalogSelect
                  label={`Sección · ${STATUS_LABELS[currentStatus] || currentStatus}`}
                  testid="repair-section-header"
                  testidManage="repair-section-header-manage"
                  testidAdd="repair-section-header-add"
                  value={form.section}
                  onChange={(v) => set("section", v)}
                  items={visibleSectionNames}
                  onItemsChange={handleSectionItemsChange}
                  placeholderAdd={`Añadir sección a "${STATUS_LABELS[currentStatus] || currentStatus}"`}
                  hideManage
                />
              </div>
              <div>
                <ContactChannelSelect
                  value={form.customer_contact_channel}
                  onChange={(v) => set("customer_contact_channel", v)}
                  catalog={contactChannelCatalog}
                  onCatalogChange={setContactChannelCatalog}
                  hideManage
                />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[.14em] text-[#3F5222] font-semibold block">
                  Estado
                </label>
                <select
                  value={order.status}
                  onChange={(e) => changeStatus(e.target.value)}
                  data-testid="repair-status-quick"
                  className="mt-1 ds-select font-mono uppercase font-semibold"
                  style={{ background: "#EFF3E6", borderColor: "#c8d6a5", color: "#3F5222" }}
                >
                  {STATUS_ORDER.map((s) => (
                    <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Row 1 — boxes 1, 2, 3 */}
      <section className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* BOX 1 — Datos del cliente */}
        <Box title="Datos del cliente" testid="repair-box-customer">
          {isNew && !customer ? (
            <CustomerPicker
              customers={customers}
              onSelect={(c) => {
                set("customer_id", c.id);
                setCustomer(c);
              }}
              showNewCustomer={showNewCustomer}
              setShowNewCustomer={setShowNewCustomer}
              newCustomer={newCustomer}
              setNewCustomer={setNewCustomer}
              onCreate={createInlineCustomer}
            />
          ) : customer ? (
            <div className="space-y-2 text-sm">
              <RO label="Nº cliente" value={customer.code} mono />
              <RO label="NIF / DNI / CIF" value={customer.tax_id} mono />
              <RO label="Nombre" value={customer.name} strong />
              <RO label="Teléfono" value={customer.phone} mono />
              <RO label="Email" value={customer.email} />
              <RO label="Dirección" value={customer.address} />
              <RO label="Código postal" value={customer.postal_code} mono />
              <RO label="Población" value={customer.city} />
              <div className="pt-2 border-t border-[#E5E5E0]">
                <Link
                  to={`/customers/${customer.id}`}
                  className="text-xs font-mono uppercase tracking-widest text-[#6E8B3D] hover:underline"
                >
                  Abrir ficha del cliente →
                </Link>
                {isNew && (
                  <button
                    type="button"
                    onClick={() => {
                      setCustomer(null);
                      set("customer_id", "");
                    }}
                    data-testid="repair-customer-change"
                    className="block mt-1 text-xs text-[#525252] hover:text-[#111110]"
                  >
                    Cambiar cliente
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="text-sm text-[#525252]">No hay cliente asociado.</div>
          )}
        </Box>

        {/* BOX 2 — Datos del dispositivo */}
        <Box title="Datos del dispositivo" testid="repair-box-device">
          <div className="space-y-3">
            {!isNew && order && (
              <Field
                label="Número de reparación"
                value={order.code}
                disabled
                mono
              />
            )}
            <DeviceModelSelect
              deviceType={form.device_type}
              model={form.model}
              onDeviceChange={(v) => set("device_type", v)}
              onModelChange={(v) => set("model", v)}
              catalog={deviceCatalog}
              required
            />
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
                Síntomas
              </label>
              <textarea
                value={form.issue}
                onChange={(e) => set("issue", e.target.value)}
                rows={4}
                placeholder="Problema indicado por el cliente..."
                data-testid="repair-issue"
                className="mt-1 w-full ds-card focus:border-[#6E8B3D] focus:ring-1 focus:ring-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
                Diagnóstico técnico
              </label>
              <textarea
                value={form.diagnosis}
                onChange={(e) => set("diagnosis", e.target.value)}
                rows={4}
                placeholder="Diagnóstico realizado por el técnico..."
                data-testid="repair-diagnosis"
                className="mt-1 w-full ds-card focus:border-[#6E8B3D] focus:ring-1 focus:ring-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
                Número de serie / IMEI <span className="text-red-600">*</span>
              </label>
              <input
                required
                value={form.serial}
                onChange={(e) => set("serial", (e.target.value || "").toUpperCase())}
                placeholder="Obligatorio"
                data-testid="repair-serial"
                autoCapitalize="characters"
                spellCheck={false}
                className="mt-1 w-full ds-card focus:border-[#6E8B3D] focus:ring-1 focus:ring-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none font-mono uppercase"
              />
              {!form.serial && (
                <div className="text-[10px] text-amber-700 mt-1">Campo obligatorio.</div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field
                label="Referencia cliente"
                value={form.customer_reference}
                onChange={(v) => set("customer_reference", v)}
                testid="repair-customer-ref"
              />
              <Field
                label="Caja cliente"
                value={form.accessories}
                onChange={(v) => set("accessories", v)}
                placeholder="Caja, funda, cargador..."
                testid="repair-customer-box"
              />
            </div>
            <EditableCatalogSelect
              label="Copia de seguridad"
              required
              testid="repair-backup-status"
              testidManage="repair-backup-status-manage"
              testidAdd="repair-backup-status-add"
              value={form.backup_status}
              onChange={(v) => set("backup_status", v)}
              items={backupCatalog}
              onItemsChange={(items) => updateCatalog("backup_status_catalog", items)}
              placeholderAdd="Añadir opción"
              placeholder="Seleccionar opción"
              hideManage
            />
            {!form.backup_status && (
              <div className="text-[10px] text-amber-700 -mt-2" data-testid="repair-backup-status-warning">
                Campo obligatorio. Selecciona una opción de copia de seguridad antes de guardar.
              </div>
            )}
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
                Estado de la contraseña
              </label>
              <select
                value={form.password_status}
                onChange={(e) => set("password_status", e.target.value)}
                data-testid="repair-password-status"
                className="mt-1 w-full ds-card focus:border-[#6E8B3D] focus:ring-1 focus:ring-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none"
              >
                <option value="">— Sin especificar —</option>
                {pwdCatalog.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
                Password {(form.password_status || "").trim().toLowerCase() === "cliente deja contraseña" && (
                  <span className="text-red-600">*</span>
                )}
              </label>
              <div className="mt-1 flex items-stretch">
                <input
                  type={showPwd ? "text" : "password"}
                  value={form.device_password}
                  onChange={(e) => set("device_password", e.target.value)}
                  placeholder={
                    (form.password_status || "").trim().toLowerCase() === "cliente deja contraseña"
                      ? "Obligatorio — el cliente dejó la contraseña"
                      : "Contraseña del equipo (opcional)"
                  }
                  data-testid="repair-password"
                  className={`flex-1 bg-white border focus:border-[#6E8B3D] focus:ring-1 focus:ring-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none font-mono ${
                    (form.password_status || "").trim().toLowerCase() === "cliente deja contraseña" &&
                    !(form.device_password || "").trim()
                      ? "border-red-400 bg-red-50"
                      : "border-[#E5E5E0]"
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(!showPwd)}
                  data-testid="repair-password-toggle"
                  className="px-3 border border-l-0 border-[#E5E5E0] hover:bg-[#F7F7F4]"
                  title={showPwd ? "Ocultar" : "Mostrar"}
                >
                  {showPwd ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
              {(form.password_status || "").trim().toLowerCase() === "cliente deja contraseña" &&
                !(form.device_password || "").trim() && (
                  <div className="text-[10px] text-red-600 mt-1">
                    Contraseña obligatoria con este estado.
                  </div>
                )}
            </div>
          </div>
        </Box>

        {/* BOX 3 — Entrada y técnico */}
        <Box title="Entrada y técnico" testid="repair-box-entry">
          <div className="space-y-3">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
                Fecha de entrada
              </label>
              <div
                data-testid="repair-entry-date"
                title="Momento exacto de creación de la ficha · Europe/Madrid (no editable)"
                className="mt-1 w-full ds-card px-3 py-2 text-sm font-mono bg-[#F7F7F4] text-[#111110] cursor-default select-text"
              >
                {isNew ? "Se asignará al guardar" : formatMadridDateTime(order?.created_at)}
              </div>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
                Técnico designado
              </label>
              <select
                value={form.assigned_to}
                onChange={(e) => set("assigned_to", e.target.value)}
                data-testid="repair-tech"
                className="mt-1 w-full ds-card focus:border-[#6E8B3D] focus:ring-1 focus:ring-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none"
              >
                <option value="">— Sin asignar —</option>
                {activeTechnicians.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}{t.specialty ? ` · ${t.specialty}` : ""}
                  </option>
                ))}
              </select>
            </div>

            {(() => {
              // Iter 161 — Fecha estimada: fecha obligatoria, hora opcional (sin hora → cita de día completo).
              const [pDate, pTime] = (form.scheduled_at || "").split("T");
              const setPromised = (d, t) => set("scheduled_at", d ? (t ? `${d}T${t}` : d) : "");
              return (
                <div className="grid grid-cols-3 gap-2" data-testid="repair-promised-block">
                  <div className={pTime ? "col-span-1" : "col-span-2"}>
                    <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
                      Fecha estimada
                    </label>
                    <input
                      type="date"
                      value={pDate || ""}
                      onChange={(e) => setPromised(e.target.value, pTime)}
                      data-testid="repair-scheduled-at"
                      className="mt-1 w-full ds-card focus:border-[#6E8B3D] focus:ring-1 focus:ring-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none font-mono"
                    />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
                      Hora <span className="normal-case tracking-normal text-[#A8A8A0]">(opcional)</span>
                    </label>
                    <input
                      type="time"
                      value={pTime || ""}
                      disabled={!pDate}
                      onChange={(e) => setPromised(pDate, e.target.value)}
                      data-testid="repair-scheduled-time"
                      className="mt-1 w-full ds-card focus:border-[#6E8B3D] focus:ring-1 focus:ring-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none font-mono disabled:opacity-50"
                    />
                  </div>
                  {pTime && (
                    <div>
                      <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
                        Duración (min)
                      </label>
                      <input
                        type="number"
                        min={15}
                        step={15}
                        value={form.scheduled_duration_min || 60}
                        onChange={(e) => set("scheduled_duration_min", Number(e.target.value) || 60)}
                        data-testid="repair-scheduled-duration"
                        className="mt-1 w-full ds-card focus:border-[#6E8B3D] focus:ring-1 focus:ring-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none font-mono"
                      />
                    </div>
                  )}
                  {pDate && (
                    <div className="col-span-3 text-[10px] text-[#6E8B3D] font-mono mt-1" data-testid="repair-promised-hint">
                      ✓ Cita única "FECHA ESTIMADA · {order?.code || "REP"}" en el Calendario{form.assigned_to ? " del técnico" : " del taller"}{pTime ? "" : " (día completo)"} · sincronizada en ambos sentidos
                    </div>
                  )}
                </div>
              );
            })()}

            <div className="pt-2">
              {(() => {
                // Iter 110 — Color dinámico del botón "Situación" según ciclo del presupuesto.
                // Prioridad: aceptado > rechazado > enviado (pendiente respuesta) > neutro.
                const est = order?.estimate_status || "";
                const sit = order?.situation_main || "";
                let toneClass = "border-[#111110] hover:bg-[#111110] hover:text-white";
                let toneTestState = "neutral";
                if (sit === "accept" || est === "accepted") {
                  toneClass = "bg-green-100 border-green-600 text-green-900 hover:bg-green-200";
                  toneTestState = "accepted";
                } else if (sit === "reject" || est === "rejected") {
                  toneClass = "bg-red-100 border-red-500 text-red-900 hover:bg-red-200";
                  toneTestState = "rejected";
                } else if (est === "sent") {
                  toneClass = "bg-yellow-100 border-yellow-500 text-yellow-900 hover:bg-yellow-200";
                  toneTestState = "sent";
                }
                return (
                  <button
                    type="button"
                    onClick={() => setShowSituation(true)}
                    disabled={isNew}
                    data-testid="repair-open-situation"
                    data-estimate-state={toneTestState}
                    className={`w-full inline-flex items-center justify-center gap-2 border-2 px-4 py-2.5 text-sm font-semibold disabled:opacity-50 transition-colors ${toneClass}`}
                  >
                    Situación
                    {order?.situation_main && (
                      <SituationBadge situation={{
                        main: order.situation_main,
                        detail_label: order.situation_detail_label,
                      }} />
                    )}
                  </button>
                );
              })()}
              {isNew && (
                <div className="text-[10px] text-amber-700 mt-2 text-center">
                  Guarda la reparación primero para gestionar la situación.
                </div>
              )}
            </div>

            <div className="pt-2">
              {(() => {
                // Iter 170 — el botón refleja la clasificación de los comentarios del seguimiento
                const tl = order?.timeline || [];
                const hasBilling = tl.some((e) => e.is_billing);
                const hasLab = tl.some((e) => e.is_laboratory);
                const tlClass = hasBilling && hasLab ? "tl-btn-both" : hasBilling ? "tl-btn-billing" : hasLab ? "tl-btn-lab" : "";
                return (
              <button
                type="button"
                onClick={() => setShowTimeline(true)}
                disabled={isNew}
                data-testid="repair-open-timeline"
                data-billing={hasBilling || undefined}
                data-laboratory={hasLab || undefined}
                className={`w-full inline-flex items-center justify-center gap-2 bg-[#111110] hover:bg-[#6E8B3D] text-white px-4 py-2.5 text-sm font-semibold disabled:opacity-50 transition-colors ${tlClass}`}
              >
                <History size={14} />
                Seguimiento interno
                {tlClass && (
                  <span className={`tl-tag ${hasBilling && hasLab ? "tl-tag-both" : hasBilling ? "tl-tag-billing" : "tl-tag-lab"}`} data-testid="repair-timeline-tag">
                    {hasBilling && hasLab && <span className="half" />}
                    {hasBilling && hasLab ? "Fact. · Lab." : hasBilling ? "Facturación" : "Laboratorio"}
                  </span>
                )}
                {order?.timeline?.length ? (
                  <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-[18px] px-1 bg-white text-[#111110] text-[10px] font-mono font-bold">
                    {order.timeline.length}
                  </span>
                ) : null}
              </button>
                );
              })()}
              {isNew && (
                <div className="text-[10px] text-amber-700 mt-2 text-center">
                  Guarda la reparación primero para acceder al seguimiento.
                </div>
              )}
            </div>

            <div className="pt-2">
              <button
                type="button"
                onClick={openWhatsApp}
                disabled={isNew}
                data-testid="repair-open-whatsapp"
                title="Abrir chat de WhatsApp con el cliente"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1EBE5D] text-white px-4 py-2.5 text-sm font-semibold disabled:opacity-50 transition-colors"
              >
                <MessageCircle size={14} />
                WhatsApp
              </button>
            </div>

            <div className="pt-2">
              <button
                type="button"
                onClick={() => setShowNotify(true)}
                disabled={isNew}
                data-testid="repair-open-notify"
                title="Enviar aviso al cliente: equipo listo (con/sin reparación)"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#6E8B3D] hover:bg-[#556C2E] text-white px-4 py-2.5 text-sm font-semibold disabled:opacity-50 transition-colors"
              >
                <Send size={14} />
                Enviar aviso al cliente
              </button>
            </div>

            <CustomerMessageBox
              isNew={isNew}
              repairId={id}
              customer={customer}
              onSent={(updated) => setOrder(updated)}
            />
          </div>
        </Box>
      </section>

      {/* Row 2 — boxes 4, 5, 6 */}
      <section className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <BoxItems
          repair={order}
          customer={customer}
          isNew={isNew}
          repairId={id}
          onUpdated={(o) => { setOrder(o); setDirty(false); }}
          onDirty={() => setDirty(true)}
        />
        <BoxDocuments
          repair={order}
          isNew={isNew}
          repairId={id}
          onUpdated={() => {
            if (id) api.get(`/repairs/${id}`).then((r) => setOrder(r.data));
          }}
        />
        <BoxSerialHistory
          serial={form.serial}
          excludeId={isNew ? undefined : id}
        />
      </section>

      {/* Iter 144 — Timeline horizontal de estados (ocupa todo el ancho) */}
      {!isNew && id && <RepairStatusTimeline repairId={id} />}

      </div>{/* /inner wrapper (max-width) */}

      {/* Modal: Confirmación RMA */}
      {rmaConfirm && (
        <Dialog onClose={() => setRmaConfirm(false)} title="Crear RMA / devolución">
          <p className="text-sm">
            Vas a clonar la reparación actual <strong>{order?.code}</strong> como RMA.
          </p>
          <ul className="mt-2 text-xs text-[#525252] list-disc pl-4 space-y-0.5">
            <li>Se creará una nueva reparación con el mismo cliente y equipo.</li>
            <li>Se vaciarán síntomas, comentarios y líneas de producto.</li>
            <li>Quedarán enlazadas como origen / RMA.</li>
          </ul>
          {order?.rma_target_id && (
            <div className="mt-3 text-[11px] text-amber-700 bg-amber-50 border border-amber-300 px-2 py-1.5">
              Esta reparación ya tiene una RMA asociada. Te llevaremos a la existente.
            </div>
          )}
          <div className="mt-4 flex items-center gap-2">
            <button
              type="button"
              onClick={() => setRmaConfirm(false)}
              className="border border-[#111110] hover:bg-[#F7F7F4] px-3 py-2 text-sm flex-1"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={doRma}
              disabled={creatingRma}
              data-testid="repair-rma-confirm"
              className="bg-amber-700 hover:bg-amber-800 text-white px-3 py-2 text-sm font-semibold flex-1 inline-flex items-center justify-center gap-1 disabled:opacity-50"
            >
              <RotateCcw size={14} /> {creatingRma ? "Creando..." : "Crear RMA"}
            </button>
          </div>
        </Dialog>
      )}

      {/* Modal: Checklist Montaje del Equipo y Pruebas */}
      {showChecklist && !isNew && id && (
        <ChecklistModal
          repairId={id}
          onClose={() => setShowChecklist(false)}
          onSaved={(level) => { setChecklistAlert(level || ""); checkIncidents(); }}
        />
      )}
      {incidents && (
        <ChecklistIncidentsModal
          repairId={id}
          incidents={incidents}
          onClose={() => setIncidents(null)}
          onOffer={(yes) => { setIncidents(null); setOfferIncidents(yes); }}
        />
      )}
      {offerIncidents && (
        <ChecklistOfferModal
          repairId={id}
          incidents={offerIncidents}
          onClose={() => setOfferIncidents(null)}
          onDone={() => {}}
        />
      )}

      {/* Iter 135 — Modal Reserva PayPal (solo cuando la reparación existe) */}
      {!isNew && id && order && (
        <ReservationModal
          repair={order}
          customer={customer}
          open={showReservation}
          onClose={() => setShowReservation(false)}
        />
      )}

      {/* Iter 129 — Modal Venta Cruzada (SOLO tras crear, nunca al editar) */}
      {crossSell && (
        <CrossSellModal
          repairId={crossSell.repairId}
          rule={crossSell.rule}
          products={crossSell.products}
          modelLabel={crossSell.modelLabel}
          onFinished={() => {
            const nav = crossSell.pendingNav;
            setCrossSell(null);
            if (nav) navigate(nav);
          }}
        />
      )}

      {/* Modal: Firma del cliente */}
      {!isNew && id && order && (
        <SignatureModal
          open={showSignature}
          onClose={() => setShowSignature(false)}
          repair={order}
          customerEmail={customer?.email || ""}
          onSigned={(updatedRepair) => setOrder(updatedRepair)}
        />
      )}

      {/* Modal: Seguimiento interno */}
      {showTimeline && !isNew && order && (
        <TimelineModal
          repair={order}
          repairId={id}
          currentUserName={null}
          onClose={() => setShowTimeline(false)}
          onUpdated={(updated) => setOrder(updated)}
        />
      )}
      {showNotify && order && (
        <NotifyCustomerDialog
          repairId={id}
          customer={customer}
          currentStatus={order.status}
          onClose={() => setShowNotify(false)}
          onSent={(updated) => setOrder(updated)}
        />
      )}
      {showSituation && !isNew && order && (
        <SituationDialog
          repairId={id}
          currentSituation={{
            main: order.situation_main || "",
            detail: order.situation_detail || "",
            detail_label: order.situation_detail_label || "",
          }}
          isAdmin={Array.isArray(me?.roles) && me.roles.includes("admin")}
          onClose={() => setShowSituation(false)}
          onSaved={(updated) => setOrder(updated)}
        />
      )}

      {/* Iter 82 — Diálogo de cambios sin guardar */}
      {unsaved.dialog}
    </div>
  );
}
